import React, { useState, useEffect } from 'react';
import { X, Camera, Monitor, Menu, Home, Briefcase } from 'lucide-react';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import { OperationalSettings } from '../types/operationalSettings';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useCustomer } from '../contexts/CustomerContext';
import { isManager } from '../config/roles';
import { logger } from '../services/logger';
import toast from 'react-hot-toast';

interface PersonalSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type SettingSection = 'detection' | 'view';

export const PersonalSettingsModal: React.FC<PersonalSettingsModalProps> = ({ open, onClose }) => {
  const { settings, updateSettings } = useOperationalSettings();
  const { isSuperAdmin, roles } = useKeycloak();
  const isSindicoUser = isManager(roles);
  const { selectedCustomer } = useCustomer();
  const isCondominio = selectedCustomer?.type === 'condominio';
  const isEmpresa = selectedCustomer?.type === 'empresa';
  const [localSettings, setLocalSettings] = useState<OperationalSettings>(settings);
  const [activeSection, setActiveSection] = useState<SettingSection>('detection');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalSettings(settings);
      setActiveSection('detection');
      setIsSidebarOpen(false);
    }
  }, [open, settings]);

  const handleSave = () => {
    updateSettings(localSettings);
    logger.setLogLevel(localSettings.logLevel);
    toast.success('Configurações salvas!');
    onClose();
  };

  const handleCancel = () => {
    setLocalSettings(settings);
    onClose();
  };

  const menuItems = [
    { id: 'detection' as SettingSection, icon: Camera, label: 'Detecção', description: 'Detecção facial' },
    ...((isSuperAdmin || isSindicoUser) ? [{ id: 'view' as SettingSection, icon: Monitor, label: 'Visualização', description: 'Alternar modo de visão' }] : [])
  ];

  const handleSectionClick = (section: SettingSection) => {
    setActiveSection(section);
    setIsSidebarOpen(false);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'detection':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1 flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Detecção de Rosto
              </h3>
              <p className="text-sm text-gray-500">Configure como a detecção facial funciona ao carregar fotos</p>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                <input
                  type="checkbox"
                  checked={localSettings.autoFaceDetection}
                  onChange={(e) => setLocalSettings({ ...localSettings, autoFaceDetection: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-200">Detecção Automática</div>
                  <p className="text-sm text-gray-400 mt-1">
                    Detecta e centraliza rostos automaticamente ao carregar fotos.
                    Desabilite para melhorar performance em ambientes com recursos limitados.
                  </p>
                </div>
              </label>
            </div>

            <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <p className="text-xs text-amber-200">
                <strong>Nota:</strong> Quando desabilitado, você pode clicar no botão
                "Detectar Rosto" manualmente para usar a detecção quando necessário.
              </p>
            </div>

            <div className="flex gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <svg className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-gray-400">
                Você pode alterar esta configuração a qualquer momento através do menu de usuário.
              </p>
            </div>
          </div>
        );

      case 'view': {
        const isOperationalMode = localStorage.getItem('isOperationalMode') === 'true';
        const isSindicoMode = localStorage.getItem('isSindicoMode') === 'true';
        const isMoradorMode = localStorage.getItem('isMoradorMode') === 'true';
        const isFuncionarioMode = localStorage.getItem('isFuncionarioMode') === 'true';
        const activeMode = isOperationalMode ? 'operational'
          : isSindicoMode ? 'sindico'
          : isMoradorMode ? 'morador'
          : isFuncionarioMode ? 'funcionario'
          : 'normal';

        const clearAll = () => {
          localStorage.setItem('isOperationalMode', 'false');
          localStorage.setItem('isSindicoMode', 'false');
          localStorage.setItem('isMoradorMode', 'false');
          localStorage.setItem('isFuncionarioMode', 'false');
        };

        const viewOptions = [
          ...(!isSindicoUser ? [{
            key: 'normal',
            icon: Monitor,
            label: 'Visão Normal',
            description: 'Visão padrão de gerenciamento com acesso a todas as funcionalidades administrativas.',
            activate: () => { onClose(); clearAll(); window.location.reload(); },
          }] : []),
          ...(!isSindicoUser ? [{
            key: 'sindico',
            icon: Home,
            label: 'Visão Síndico',
            description: 'Simula a visão de um síndico — painel de gestão com métricas, desempenho dos porteiros e relatórios.',
            activate: () => {
              onClose();
              clearAll();
              localStorage.setItem('isSindicoMode', 'true');
              window.location.reload();
            },
          }] : []),
          ...(!isSindicoUser && isCondominio ? [{
            key: 'morador',
            icon: Home,
            label: 'Visão Morador',
            description: 'Simula a visão de um morador — acesso apenas à sua unidade, visitantes e informações do condomínio.',
            activate: () => {
              onClose();
              clearAll();
              localStorage.setItem('isMoradorMode', 'true');
              window.location.reload();
            },
          }] : []),
          ...(!isSindicoUser && isEmpresa ? [{
            key: 'funcionario',
            icon: Briefcase,
            label: 'Visão Funcionário',
            description: 'Simula a visão de um porteiro/funcionário — navegação simplificada com acesso a cadastros e monitoramento.',
            activate: () => {
              onClose();
              clearAll();
              localStorage.setItem('isFuncionarioMode', 'true');
              window.location.reload();
            },
          }] : []),
          {
            key: 'operational',
            icon: Monitor,
            label: 'Modo Operacional',
            description: 'Visão simplificada para porteiros e equipe operacional com controle de acesso em tempo real.',
            activate: () => {
              onClose();
              clearAll();
              localStorage.setItem('isOperationalMode', 'true');
              window.location.reload();
            },
          },
        ];

        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1 flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Visualização
              </h3>
              <p className="text-sm text-gray-500">Alternar entre os modos de visão disponíveis</p>
            </div>

            <div className="space-y-3">
              {viewOptions.map(({ key, icon: Icon, label, description, activate }) => {
                const isActive = activeMode === key;
                return (
                  <div key={key} className={`p-4 rounded-lg border transition-colors ${isActive ? 'bg-blue-500/10 border-blue-500/30' : 'bg-gray-800 border-gray-700'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-400' : 'text-gray-400'}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-200">{label}</span>
                            {isActive && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Ativo</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                        </div>
                      </div>
                      {!isActive && (
                        <button
                          onClick={activate}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors flex-shrink-0"
                        >
                          Ativar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 z-50" onClick={handleCancel} />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5 text-gray-400" />
              </button>
              <h2 className="text-lg font-semibold text-gray-200">Preferências</h2>
            </div>
            <button onClick={handleCancel} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Body — sidebar + content */}
          <div className="flex-1 flex overflow-hidden min-h-0 relative">

            {/* Mobile sidebar overlay */}
            {isSidebarOpen && (
              <div className="lg:hidden fixed inset-0 bg-black/50 z-10" onClick={() => setIsSidebarOpen(false)} />
            )}

            {/* Left Sidebar */}
            <div className={`
              ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
              lg:translate-x-0
              absolute lg:relative inset-y-0 left-0 z-20
              w-56 bg-gray-900 border-r border-gray-800
              transition-transform duration-200 ease-in-out
              flex flex-col flex-shrink-0
            `}>
              <div className="lg:hidden flex items-center justify-between p-3 border-b border-gray-800">
                <span className="text-xs font-medium text-gray-400">Menu</span>
                <button onClick={() => setIsSidebarOpen(false)} className="p-1 hover:bg-gray-800 rounded transition-colors">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
                {menuItems.map(({ id, icon: Icon, label, description }) => {
                  const isActive = activeSection === id;
                  return (
                    <button
                      key={id}
                      onClick={() => handleSectionClick(id)}
                      className={`w-full flex items-start gap-3 p-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                          : 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isActive ? 'text-blue-400' : 'text-gray-500'}`} />
                      <div className="flex-1 text-left">
                        <div className={`text-sm font-medium ${isActive ? 'text-blue-400' : 'text-gray-200'}`}>{label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              <div className="p-3 border-t border-gray-800">
                <p className="text-xs text-gray-600 text-center">Preferências do usuário</p>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-5">{renderContent()}</div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-800 flex-shrink-0">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
